import { OrthographicCamera, Text } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import { Tween, update } from '@tweenjs/tween.js';
import { useEffect, useState } from 'react';
import { Audio as ThreeAudio, AudioListener, AudioLoader, Vector3 } from 'three';
import { shuffleAndCutStack, sleep } from 'utils/helper';
import { AnswerState, EmoteDictionary, GameState, ScreenAnchor, ScreenOrientation, SoundEffect } from './enums/ThreeEnums';
import { Emote, EmoteStack, Hearts, SpriteButton } from './ThreeComponents';

export default function ThreeCanvas() {
    const [zoom, setZoom] = useState<number>(1);
    const [screenDimensions, setScreenDimensions] = useState({ width: 0, height: 0 });
    const [screenOrientation, setScreenOrientation] = useState<ScreenOrientation>();

    const [currentGameState, setCurrentGameState] = useState(GameState.BeforeGame);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [boardStack, setBoardStack] = useState<number[]>([]);
    const [playableStack, setPlayableStack] = useState<number[]>([]);
    const [handStack, setHandStack] = useState<number[]>([]);
    const [inputs, SetInputs] = useState<number[]>([]);
    const [correctAnswers, setCorrectAnswers] = useState<number[]>([]);
    const [wrongAnswers, setWrongAnswers] = useState<number[]>([]);
    const [score, setScore] = useState<number>(0);
    const [scoreCounter, setScoreCounter] = useState<number>(0);
    const [animateScore, setAnimateScore] = useState(() => (from: number, to: number) => { });
    const [localHighscore, setLocalHighscore] = useState<number>(100);
    const [totalLives] = useState(3);
    const [remainingLives, setRemainingLives] = useState(0);
    const [comboFactor, setComboFactor] = useState<number>(1);
    const [defaultComboFactor] = useState(1.0);

    const [flipBoardProgress, setFlipBoardProgress] = useState(0);
    const [flipBoardDown, setFlipBoardDown] = useState(() => () => { });
    const [flipBoardOpen, setFlipBoardUp] = useState(() => () => { });

    const [flipHandProgress, setFlipHandProgress] = useState(0);
    const [flipHandDown, setFlipHandDown] = useState(() => () => { });
    const [flipHandOpen, setFlipHandUp] = useState(() => () => { });

    const fullStack = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 27, 28, 29];
    const scoreIncrement = 100;

    window.onresize = () => { recalculateScreenOrientation() };

    useEffect(() => {
        recalculateScreenOrientation();
        setLocalHighscore(Number(localStorage.getItem('localHighscore')));

        const shuffledStack: number[] = shuffleAndCutStack(fullStack, 10);
        setPlayableStack(shuffledStack);
        setHandStack([EmoteDictionary.Play])
        setBoardStack(shuffleAndCutStack(shuffledStack, 1));

        const boardUpState = { value: 0 }
        const boardDownState = { value: 180 }
        const boardUpTween = new Tween(boardDownState).to({ value: 360 }, 400).onUpdate(() => { setFlipBoardProgress(boardDownState.value) });
        const boardDownTween = new Tween(boardUpState).to({ value: 180 }, 400).onUpdate(() => { setFlipBoardProgress(boardUpState.value) });
        setFlipBoardUp(() => () => { boardUpTween.start() });
        setFlipBoardDown(() => () => { boardDownTween.start() });

        const handUpState = { value: 0 }
        const handDownState = { value: 180 }
        const handUpTween = new Tween(handDownState).to({ value: 360 }, 400).onUpdate(() => { setFlipHandProgress(handDownState.value) });
        const handDownTween = new Tween(handUpState).to({ value: 180 }, 400).onUpdate(() => { setFlipHandProgress(handUpState.value) });
        setFlipHandUp(() => () => { handUpTween.start() });
        setFlipHandDown(() => () => { handDownTween.start() });

        setAnimateScore(() => (from: number, to: number) => {
            setScore(to);
            new Tween({ value: from }).to({ value: to }, 300).onUpdate(({ value }) => { setScoreCounter(Math.round(value)) }).start();
        });
    }, []);

    useEffect(() => {
        const input = inputs.pop();

        if (input === EmoteDictionary.Share) {
            socialShare();
            return;
        }

        (async () => {
            if (input) {
                switch (currentGameState) {
                    case GameState.EndGame: {
                        playSoundEffect(SoundEffect.Confirmation);
                        setCurrentGameState(GameState.Transition);
                        clearAnswers();
                        flipBoardDown();
                        flipHandDown();
                        await sleep(400);
                        flipBoardOpen();
                        flipHandOpen();
                        setHandStack([EmoteDictionary.Play]);
                        const shuffledStack: number[] = shuffleAndCutStack(fullStack, 10);
                        setPlayableStack(shuffledStack);
                        setBoardStack([...shuffleAndCutStack(shuffledStack, 1)]);
                        setScore(0);
                        setScoreCounter(0);
                        setComboFactor(defaultComboFactor);
                        setCurrentGameState(GameState.BeforeGame);
                        break;
                    }
                    case GameState.BeforeGame: {
                        playSoundEffect(SoundEffect.Confirmation);
                        setCurrentGameState(GameState.Transition);
                        setRemainingLives(totalLives);
                        flipHandDown();
                        await sleep(400);
                        setHandStack(playableStack);
                        flipHandOpen();
                        await sleep(600);
                        flipBoardDown();
                        await sleep(300);
                        setCurrentGameState(GameState.DuringGame);
                        break;
                    }
                    case GameState.DuringGame: {
                        if (input === boardStack[currentIndex]) {
                            playSoundEffect(SoundEffect.Correct);
                            setCorrectAnswers([...correctAnswers, currentIndex]);
                            setCurrentIndex(currentIndex + 1);
                            const newScore = score + (scoreIncrement * comboFactor);
                            animateScore(score, newScore);
                            if (newScore > localHighscore) {
                                setLocalHighscore(newScore);
                                localStorage.setItem('localHighscore', newScore.toString());
                            }
                            if (currentIndex === boardStack.length - 1) {
                                setCurrentGameState(GameState.Transition);
                                setComboFactor(Math.min(comboFactor + 0.1, 3.0));
                                flipBoardOpen();
                                await sleep(1000);
                                flipBoardDown();
                                clearAnswers();
                                await sleep(600);
                                setBoardStack([...boardStack, ...shuffleAndCutStack(playableStack, 1)]);
                                flipBoardOpen();
                                await sleep(800);
                                flipBoardDown();
                                await sleep(300);
                                setCurrentGameState(GameState.DuringGame);
                            }
                        } else {
                            playSoundEffect(SoundEffect.Wrong);
                            setCurrentGameState(GameState.Transition);
                            const lives = remainingLives - 1;
                            setComboFactor(defaultComboFactor);
                            setRemainingLives(lives);
                            setWrongAnswers([...wrongAnswers, currentIndex]);
                            if (lives > 0) {
                                flipBoardOpen();
                                await sleep(1500);
                                flipBoardDown();
                                clearAnswers();
                                await sleep(600);
                                setCurrentGameState(GameState.DuringGame);
                            } else {
                                flipBoardOpen();
                                await sleep(600);
                                flipHandDown();
                                setCurrentGameState(GameState.EndGame);
                                await sleep(600);
                                setHandStack([EmoteDictionary.Replay])
                                flipHandOpen();
                            }
                        }
                        break;
                    }
                }
            }
        })();
    }, [inputs]);

    function clearAnswers() {
        setWrongAnswers([]);
        setCorrectAnswers([]);
        setCurrentIndex(0);
    }

    function animate(time: number) {
        requestAnimationFrame(animate);
        update(time);
    }
    requestAnimationFrame(animate);

    function recalculateScreenOrientation() {
        const ratio = window.innerWidth / window.innerHeight;
        const landscapeThreshold = 0.6;
        setScreenOrientation(ratio > landscapeThreshold ? ScreenOrientation.Landscape : ScreenOrientation.Portrait);
        const zoomFactor = 1 / (1080 / Math.min(window.innerHeight, window.innerWidth));
        setZoom(zoomFactor);
        setScreenDimensions({ width: window.innerWidth / zoomFactor, height: window.innerHeight / zoomFactor });
    }

    function responsiveValue(landscapeValue: any, portraitValue: any) {
        return screenOrientation === ScreenOrientation.Landscape ? landscapeValue : portraitValue;
    }

    function screenAnchor(anchor: ScreenAnchor, landscapeOffset: [number, number, number], portraitOffset?: [number, number, number]) {
        portraitOffset = portraitOffset ?? landscapeOffset;
        const offset = responsiveValue(landscapeOffset, portraitOffset);
        switch (anchor) {
            case (ScreenAnchor.Center): {
                return new Vector3(offset[0], offset[1], offset[2]);
            }
            case (ScreenAnchor.Top): {
                return new Vector3(offset[0], (screenDimensions.height / 2) + offset[1], offset[2]);
            }
            case (ScreenAnchor.Bottom): {
                return new Vector3(offset[0], (-screenDimensions.height / 2) + offset[1], offset[2]);
            }
        }
    }

    function socialShare(data?: {}) {
        const shareData = {
            title: 'Repeat',
            text: 'Simple memory game to play in free time',
            url: 'https://repeat.glasspile.com',
            ...data
        };
        navigator.share(shareData);
    }

    const [listener, setListener] = useState<AudioListener>();
    const [audio, setAudio] = useState<ThreeAudio>();
    const [loader, setLoader] = useState<AudioLoader>();

    function playSoundEffect(soundEffect: SoundEffect) {
        const file = soundEffect;
        const mediaElement = new Audio(file);
        if (!listener || !audio || !loader) {
            const _listener = new AudioListener();
            const _audio = new ThreeAudio(_listener);
            const _loader = new AudioLoader();
            setListener(_listener);
            setAudio(_audio);
            setLoader(_loader);
            if (/(iPad|iPhone|iPod)/g.test(navigator.userAgent)) {
                _loader.load(file, (buffer) => {
                    _audio.setBuffer(buffer);
                    _audio.play();
                });
            } else {
                mediaElement.play();
                _audio.setMediaElementSource(mediaElement);
            }
        } else if (listener && loader && audio) {
            if (/(iPad|iPhone|iPod)/g.test(navigator.userAgent)) {
                loader.load(file, (buffer) => {
                    audio.setBuffer(buffer);
                    audio.play();
                });
            } else {
                mediaElement.play();
                audio.setMediaElementSource(mediaElement);
            }
        }
    }

    return (
        <Canvas>
            <OrthographicCamera makeDefault zoom={zoom} position={[0, 0, 1000]}></OrthographicCamera>
            <group position={screenAnchor(ScreenAnchor.Top, [-380, -40, 0])}>
                <SpriteButton texture={'./logo512.png'} width={responsiveValue(60, 100)} position={responsiveValue([-75, -25, 0], [-75, -45, 0])} onClick={socialShare}></SpriteButton>
                <Text position={responsiveValue([14, -10, 0], [75, -20, 0])} fontSize={responsiveValue(38, 64)} textAlign="center" anchorX="center" anchorY="middle" font="./fonts/lato-bold.woff">repeat</Text>
                <Text position={responsiveValue([14, -40, 0], [75, -68, 0])} fontSize={responsiveValue(17.8, 30)} textAlign="center" anchorX="center" anchorY="middle" font="./fonts/lato-light.woff">memory game</Text>
                <Text position={responsiveValue([-101, -70, 0], [-116, -116, 0])} fontSize={responsiveValue(30, 45)} textAlign="left" anchorX="left" anchorY="middle" font="./fonts/lato-light.woff">your highscore:</Text>
                <Text position={responsiveValue([104, -70, 0], [188, -116, 0])} fontSize={responsiveValue(30, 45)} textAlign="left" anchorX="left" anchorY="middle" font="./fonts/lato-medium.woff" onDoubleClick={() => { localStorage.removeItem('localHighscore'); setLocalHighscore(0); playSoundEffect(SoundEffect.Delete); }}>{localHighscore}</Text>
            </group>
            <group visible={currentGameState !== GameState.BeforeGame} position={screenAnchor(ScreenAnchor.Top, [480, -65, 0], [480, -85, 0])}>
                <Text position={responsiveValue([-70, 0, 0], [-100, 0, 0])} fontSize={responsiveValue(64, 108)} textAlign="right" anchorX="right" anchorY="middle" font="./fonts/lato-bold.woff">{scoreCounter}</Text>
                <Emote position={[-20, 0, 0]} width={responsiveValue(80, 120)} index={EmoteDictionary.Badge} state={AnswerState.None}></Emote>
                <Text position={[-20, 0, 0]} fontSize={responsiveValue(26, 40)} textAlign="center" anchorX="center" anchorY="middle" font="./fonts/lato-bold.woff" color="#262626">{`x${comboFactor.toFixed(1)}`}</Text>
                <group position={responsiveValue([-20, -80, 0], [-30, -100, 0])}>
                    <Hearts width={50} amount={remainingLives} spacing={60}></Hearts>
                </group>
            </group>
            <EmoteStack emotes={boardStack} flipped={flipBoardProgress} position={responsiveValue([0, 60, 0], [0, 100, 0])}
                width={responsiveValue(100, 160)} lineHeight={responsiveValue(110, 180)} maxPerLine={responsiveValue(10, 6)}
                wrongAnswers={wrongAnswers} correctAnswers={correctAnswers}></EmoteStack>
            <EmoteStack emotes={handStack} flipped={flipHandProgress} position={screenAnchor(ScreenAnchor.Bottom, [0, 80, 0], [0, 240, 0])}
                width={responsiveValue(100, 180)} lineHeight={190} maxPerLine={responsiveValue(10, 5)}
                onEmoteClick={(i) => SetInputs([...inputs, i])}></EmoteStack>
        </Canvas >
    )
}

